import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  CopyIcon,
  EditIcon,
  FreezeIcon,
  LeftArrowIcon,
  RevealIcon,
  TopUpIcon,
  UnfreezeIcon,
} from '../../../assets/icons/icons';
import { useCarouselCards } from '../../../assets/hooks/useCardData';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import {
  formatMaskedPan,
  getCardStyleName,
} from '../../../assets/constants/cardSensitive';
import { isPhysicalCardType } from '../../../assets/constants/cardTypes';
import {
  calculateMinCardIssuePrice,
  formatCardMoneyAmount,
  getCardIssueFundingProgress,
  getSelectedCardId,
  isCardIssueFundingCovered,
  isCardFrozen,
  isActivatingCardStatus,
  isBlockedCardStatus,
  isNotIssuedCardStatus,
  refreshCardById,
  revealCardSecretData,
  setSelectedCardId,
  updateRemoteCardDetails,
  updateRemoteCardStatus,
} from '../../../assets/services/cards';
import { MIN_CARD_BALANCE_TOP_UP_USD } from '../../../assets/constants/depositData';
import {
  cancelDepositFlow,
  setDepositCard,
  setDepositReturnPath,
} from '../../../assets/services/deposit';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import { confirmCarouselCardReturn } from '../../../assets/services/carouselReturn';
import BankCard from '../../BankCard/BankCard';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import CardAppearanceSheet from '../CardAppearanceSheet/CardAppearanceSheet';
import FreezeCardSheet from '../FreezeCardSheet/FreezeCardSheet';
import RenameCardSheet from '../RenameCardSheet/RenameCardSheet';
import './MyCard.css';

const CARD_ENTRY_TRANSITION = {
  type: 'spring',
  stiffness: 360,
  damping: 34,
  mass: 0.8,
  // Keeps the native fallback close to the spring's measured settle time.
  duration: 0.5,
};

const SECRET_REVEAL_STEP_MS = 26;
const WALLET_GUIDE_TRANSITION = {
  duration: 0.34,
  ease: [0.22, 1, 0.36, 1],
};
const CARD_ACTION_ERROR = 'Request failed';

const CrossIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    {...props}
  >
    <path
      fill="currentColor"
      d="M17.293 5.293a1 1 0 1 1 1.414 1.414L13.414 12l5.293 5.293a1 1 0 1 1-1.414 1.414L12 13.414l-5.293 5.293a1 1 0 1 1-1.414-1.414L10.586 12 5.293 6.707a1 1 0 1 1 1.414-1.414L12 10.586z"
    />
  </svg>
);

const PINIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    {...props}
  >
    <path
      fill="currentColor"
      d="M5.293 8.515a1 1 0 1 1 1.748.97L6.199 11H7a1 1 0 0 1 0 2h-.8l.841 1.515a1 1 0 0 1-1.748.97L4.5 14.057l-.793 1.428a1 1 0 1 1-1.748-.97L2.801 13H2a1 1 0 1 1 0-2h.8L1.96 9.485a1 1 0 0 1 1.748-.97L4.5 9.94zm7.5 0a1 1 0 1 1 1.748.97L13.699 11h.801a1 1 0 1 1 0 2h-.8l.841 1.515a1 1 0 0 1-1.748.97L12 14.057l-.793 1.428a1 1 0 1 1-1.748-.97L10.301 13H9.5a1 1 0 1 1 0-2h.8L9.46 9.485a1 1 0 0 1 1.748-.97L12 9.94zm7.5 0a1 1 0 1 1 1.748.97L21.199 11H22a1 1 0 1 1 0 2h-.8l.841 1.515a1 1 0 0 1-1.748.97l-.793-1.428-.793 1.428a1 1 0 1 1-1.748-.97L17.801 13H17a1 1 0 1 1 0-2h.8l-.841-1.515a1 1 0 0 1 1.748-.97L19.5 9.94z"
    />
  </svg>
);

function getWalletProvider(webApp) {
  const platform = String(webApp?.platform || '').toLowerCase();
  const userAgent = typeof navigator === 'undefined'
    ? ''
    : String(navigator.userAgent || '').toLowerCase();
  const device = `${platform} ${userAgent}`;

  if (device.includes('android')) {
    return 'google';
  }

  if (
    device.includes('ios')
    || device.includes('iphone')
    || device.includes('ipad')
    || device.includes('macos')
    || device.includes('macintosh')
  ) {
    return 'apple';
  }

  return 'google';
}

function isRevealableChar(char) {
  return char !== ' ' && char !== '/';
}

function getRevealableCharCount(value) {
  return Array.from(value).filter(isRevealableChar).length;
}

function formatVisiblePan(value) {
  const digits = String(value ?? '').replace(/\D/g, '');

  if (digits.length === 16) {
    return digits.match(/.{1,4}/g).join('  ');
  }

  return String(value ?? '');
}

function alignPanLastFour(pan, lastFour) {
  if (!pan || !lastFour) {
    return pan;
  }

  return String(pan).replace(/\d{4}(?=\D*$)/, String(lastFour));
}

async function copyToClipboard(value) {
  const text = String(value ?? '');

  if (!text) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to the legacy path below.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function AnimatedSecretValue({
  value,
  placeholder,
  isRevealed,
}) {
  const shouldReduceMotion = useReducedMotion();
  const targetValue = String(value ?? '');
  const hiddenValue = String(placeholder ?? targetValue.replace(/[^\s/]/g, '•'));
  const targetChars = Array.from(targetValue);
  const hiddenChars = Array.from(hiddenValue);
  const revealableCount = getRevealableCharCount(targetValue);
  const [visibleCount, setVisibleCount] = useState(() => (
    isRevealed && shouldReduceMotion ? revealableCount : 0
  ));

  useEffect(() => {
    if (!isRevealed) {
      setVisibleCount(0);
      return undefined;
    }

    if (shouldReduceMotion) {
      setVisibleCount(revealableCount);
      return undefined;
    }

    setVisibleCount(0);

    const timer = window.setInterval(() => {
      setVisibleCount((current) => {
        if (current >= revealableCount) {
          window.clearInterval(timer);
          return current;
        }

        return current + 1;
      });
    }, SECRET_REVEAL_STEP_MS);

    return () => window.clearInterval(timer);
  }, [isRevealed, revealableCount, shouldReduceMotion, targetValue]);

  if (!isRevealed) {
    return hiddenChars.map((char, index) => (
      <span key={`${index}-${char}`} className="my-card__details-char">
        {char}
      </span>
    ));
  }

  let revealIndex = 0;

  return targetChars.map((char, index) => {
    const canReveal = isRevealableChar(char);
    let isVisible = !canReveal;

    if (canReveal) {
      revealIndex += 1;
      isVisible = revealIndex <= visibleCount;
    }

    const displayChar = isVisible ? char : (hiddenChars[index] ?? '•');

    return (
      <span
        key={`${index}-${displayChar}-${isVisible ? 'revealed' : 'hidden'}`}
        className={
          isVisible
            ? 'my-card__details-char my-card__details-char--revealed'
            : 'my-card__details-char'
        }
      >
        {displayChar}
      </span>
    );
  });
}

function MyCardHeader({ cardCopy, onBack, onEdit }) {
  const handleBack = () => {
    triggerImpactHaptic('light');
    onBack?.();
  };

  return (
    <header className="my-card__header">
      <button type="button" className="my-card__back" onClick={handleBack}>
        <LeftArrowIcon className="my-card__back-icon" />
        <span className="my-card__title">{cardCopy.myCard}</span>
      </button>
      <div className="my-card__header-actions">
        {onEdit && (
          <button type="button" className="my-card__edit" onClick={onEdit}>
            <EditIcon className="my-card__edit-icon" />
            <span>{cardCopy.edit}</span>
          </button>
        )}
      </div>
    </header>
  );
}

function CancelIssueSheet({ cardCopy, common, onConfirm, onCancel }) {
  return (
    <BottomSheet
      className="my-card-cancel-issue-sheet"
      panelClassName="my-card-cancel-issue-sheet__panel"
      titleId="my-card-cancel-issue-title"
      onClose={onCancel}
    >
      <div className="my-card-cancel-issue-sheet__content">
        <h2 id="my-card-cancel-issue-title" className="my-card-cancel-issue-sheet__title">
          {cardCopy.cancelRequestQuestion}
        </h2>
        <p className="my-card-cancel-issue-sheet__text">
          {cardCopy.cancelRequestText}
        </p>
      </div>

      <div className="my-card-cancel-issue-sheet__actions">
        <button
          type="button"
          className="my-card-cancel-issue-sheet__secondary"
          onClick={onCancel}
        >
          {common.cancel}
        </button>
        <BottomActionButton
          className="my-card-cancel-issue-sheet__confirm"
          onClick={onConfirm}
        >
          {cardCopy.cancelRequest}
        </BottomActionButton>
      </div>
    </BottomSheet>
  );
}

function PendingCardPanel({
  card,
  cardCopy,
  title,
  text,
  onContinue,
  onCancelIssue,
  showActions = true,
}) {
  return (
    <div className="my-card__pending-panel">
      <div className="my-card__pending-copy">
        <h2 className="my-card__pending-title">{title}</h2>
        <p className="my-card__pending-text">
          {text}
        </p>
      </div>

      <div className="my-card__pending-meta">
        <div className="my-card__pending-row">
          <span>{cardCopy.nameOnCard}</span>
          <strong>{card.nameOnCard || cardCopy.pending}</strong>
        </div>
      </div>

      {showActions && (
        <>
          <button type="button" className="my-card__continue-issue" onClick={onContinue}>
            {cardCopy.topUpToActivate}
          </button>
          {onCancelIssue && (
            <button type="button" className="my-card__cancel-issue" onClick={onCancelIssue}>
              {cardCopy.cancelRequest}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function BlockedCardPanel({ cardCopy }) {
  return (
    <div className="my-card__pending-panel">
      <div className="my-card__pending-copy">
        <h2 className="my-card__pending-title">{cardCopy.blockedTitle}</h2>
        <p className="my-card__pending-text">
          {cardCopy.blockedNotice}
        </p>
      </div>
    </div>
  );
}

export function CardPinAction({ cardCopy, onClick }) {
  return (
    <button
      type="button"
      className="my-card__action my-card__action--wide"
      onClick={onClick}
    >
      <span className="my-card__action-icon" aria-hidden="true">
        <PINIcon className="my-card__action-svg" />
      </span>
      <span className="my-card__action-copy">
        <span className="my-card__action-title">{cardCopy.changePin}</span>
        <span className="my-card__action-value">{cardCopy.changePinDescription}</span>
      </span>
    </button>
  );
}

export function CardDetailsPanel({
  card,
  cardCopy,
  common,
  revealedData,
  isRevealing,
  onReveal,
  onCopy,
}) {
  const isRevealed = Boolean(revealedData);
  const hiddenPan = formatMaskedPan(card.lastFour);
  const panValue = isRevealed ? formatVisiblePan(revealedData.pan) : hiddenPan;
  const expiryValue = revealedData?.expiry || '••/••';
  const cvvValue = revealedData?.cvv || '•••';
  const pinValue = revealedData?.pinCode || '••••••';
  const nameOnCard = card.nameOnCard || card.fullName || '';
  const isPhysicalCard = isPhysicalCardType(card.type);

  const renderField = ({
    label,
    value,
    placeholder,
    copyValue,
    small = false,
    action = null,
    className = '',
    alwaysCopyable = false,
    alwaysVisible = false,
    instantValue = false,
  }) => {
    const isFieldCopyable = alwaysCopyable || isRevealed;
    const isValueVisible = alwaysVisible || isRevealed;
    const valueClassName = small
      ? 'my-card__details-copy my-card__details-copy--small'
      : 'my-card__details-copy';
    const fieldClassName = [
      'my-card__details-field',
      className,
      isFieldCopyable ? 'my-card__details-field--copyable' : '',
    ].filter(Boolean).join(' ');
    const valueContent = (
      <span className={valueClassName}>
        <span className="my-card__details-copy-text">
          {instantValue ? (
            value
          ) : (
            <AnimatedSecretValue
              value={value}
              placeholder={placeholder}
              isRevealed={isValueVisible}
            />
          )}
        </span>
        {isFieldCopyable ? (
          <CopyIcon className="my-card__details-copy-icon" />
        ) : action}
      </span>
    );

    if (isFieldCopyable) {
      return (
        <button
          type="button"
          className={fieldClassName}
          onClick={() => onCopy(label, copyValue ?? value)}
          aria-label={`${common.copy} ${label.toLowerCase()}`}
        >
          <span className="my-card__details-label">{label}</span>
          {valueContent}
        </button>
      );
    }

    return (
      <div className={fieldClassName}>
        <span className="my-card__details-label">{label}</span>
        {valueContent}
      </div>
    );
  };

  return (
    <div className={isRevealed ? 'my-card__details' : 'my-card__details my-card__details--masked'}>
      <div className="my-card__details-inner">
        <div className="my-card__details-grid">
          {renderField({
            label: cardCopy.cardNumber,
            value: panValue,
            placeholder: hiddenPan,
            copyValue: revealedData?.pan,
            className: 'my-card__details-field--number',
            action: (
              <button
                type="button"
                className="my-card__reveal"
                aria-label={cardCopy.revealDetails}
                onClick={onReveal}
                disabled={isRevealing}
              >
                {isRevealing ? (
                  <span className="my-card__reveal-loader" aria-hidden="true" />
                ) : (
                  <RevealIcon className="my-card__reveal-icon" />
                )}
              </button>
            ),
          })}
          {renderField({
            label: cardCopy.expiry,
            value: expiryValue,
            placeholder: '••/••',
            small: true,
          })}
          {renderField({
            label: common.copiedLabels.cvv,
            value: cvvValue,
            placeholder: '•••',
            small: true,
            className: 'my-card__details-field--cvv',
          })}
          {isPhysicalCard ? renderField({
            label: cardCopy.pin,
            value: pinValue,
            placeholder: '••••••',
            copyValue: revealedData?.pinCode,
            small: true,
            className: 'my-card__details-field--pin',
          }) : null}
          {renderField({
            label: cardCopy.nameOnCard,
            value: nameOnCard,
            placeholder: nameOnCard,
            copyValue: nameOnCard,
            small: true,
            className: 'my-card__details-field--name',
            alwaysCopyable: true,
            alwaysVisible: true,
            instantValue: true,
          })}
        </div>
      </div>
    </div>
  );
}

function CardIssueDetails({ card, cardCopy, common, onCopy }) {
  const cardIssueDetails = [
    [cardCopy.issuingCountry, card.address?.country],
    [cardCopy.city, card.address?.city],
    [cardCopy.address, card.address?.address],
    [cardCopy.postalCode, card.address?.postalCode],
  ].filter(([, value]) => value);

  if (cardIssueDetails.length === 0) {
    return null;
  }

  return (
    <section className="my-card__issue-details" aria-label={cardCopy.cardDetails}>
      {cardIssueDetails.map(([label, value]) => (
        <button
          type="button"
          className="my-card__issue-field"
          key={label}
          onClick={() => onCopy(label, value)}
          aria-label={`${common.copy} ${label.toLowerCase()}`}
        >
          <span className="my-card__issue-label">{label}</span>
          <span className="my-card__issue-value">
            <span className="my-card__issue-value-text">{value}</span>
            <CopyIcon className="my-card__issue-copy-icon" />
          </span>
        </button>
      ))}
    </section>
  );
}

function WalletGuideModal({
  provider,
  card,
  cardCopy,
  common,
  walletGuideCopy,
  revealedData,
  isRevealing,
  onReveal,
  onCopy,
  onClose,
}) {
  const { t } = useContext(TranslationContext);
  const [activeStep, setActiveStep] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const guide = provider === 'apple'
    ? {
      payName: 'Apple Pay',
      title: walletGuideCopy.addAppleTitle,
      subtitle: walletGuideCopy.addAppleSubtitle,
      steps: [
        {
          title: walletGuideCopy.startWallet,
          screenType: 'apple-start',
          bullets: walletGuideCopy.appleStartBullets,
        },
        {
          title: walletGuideCopy.enterDetails,
          screenType: 'apple-details',
          bullets: walletGuideCopy.appleDetailsBullets,
        },
        {
          title: walletGuideCopy.confirmWithCode,
          screenType: 'apple-code',
          bullets: walletGuideCopy.appleCodeBullets,
        },
      ],
    }
    : {
      payName: 'Google Pay',
      title: walletGuideCopy.addGoogleTitle,
      subtitle: walletGuideCopy.addGoogleSubtitle,
      steps: [
        {
          title: walletGuideCopy.startGoogle,
          screenType: 'google-start',
          bullets: walletGuideCopy.googleStartBullets,
        },
        {
          title: walletGuideCopy.enterDetails,
          screenType: 'google-details',
          bullets: walletGuideCopy.googleDetailsBullets,
        },
        {
          title: walletGuideCopy.confirmWithCode,
          screenType: 'google-code',
          bullets: walletGuideCopy.googleCodeBullets,
        },
      ],
    };
  const steps = guide.steps;
  const currentStep = steps[activeStep] ?? steps[0];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;
  const isDetailsStep = currentStep?.screenType?.includes('details');
  const rootMotion = shouldReduceMotion
    ? {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.12 },
    }
    : {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.18 },
    };
  const panelMotion = shouldReduceMotion
    ? {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.12 },
    }
    : {
      initial: {
        opacity: 0,
        transform: 'translate3d(0, 28px, 0)',
        filter: 'blur(8px)',
      },
      animate: {
        opacity: 1,
        transform: 'translate3d(0, 0px, 0)',
        filter: 'blur(0px)',
      },
      exit: {
        opacity: 0,
        transform: 'translate3d(0, 18px, 0)',
        filter: 'blur(8px)',
      },
      transition: WALLET_GUIDE_TRANSITION,
    };

  const handlePrevious = () => {
    if (isFirstStep) return;

    triggerImpactHaptic('light');
    setActiveStep((current) => Math.max(0, current - 1));
  };

  const handleNext = () => {
    if (isLastStep) {
      onClose();
      return;
    }

    triggerImpactHaptic('light');
    setActiveStep((current) => Math.min(steps.length - 1, current + 1));
  };

  return (
    <motion.div
      className="my-card-wallet-guide"
      role="presentation"
      onClick={onClose}
      {...rootMotion}
    >
      <motion.section
        className="my-card-wallet-guide__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="my-card-wallet-guide-title"
        onClick={(event) => event.stopPropagation()}
        {...panelMotion}
      >
        <header className="my-card-wallet-guide__header">
          <div className="my-card-wallet-guide__heading">
            <p className="my-card-wallet-guide__eyebrow">{guide.payName}</p>
            <h2 id="my-card-wallet-guide-title" className="my-card-wallet-guide__title">
              {guide.title}
            </h2>
            <p className="my-card-wallet-guide__subtitle">{guide.subtitle}</p>
          </div>
          <button
            type="button"
            className="my-card-wallet-guide__close"
            aria-label={cardCopy.closeDetails}
            onClick={onClose}
          >
            <CrossIcon className="my-card-wallet-guide__close-icon" />
          </button>
        </header>

        <div className="my-card-wallet-guide__body">
          <article className="my-card-wallet-guide__step">
            <div className="my-card-wallet-guide__progress">
              <span>
                {t('common.stepOf', { current: activeStep + 1, total: steps.length })}
              </span>
              <div className="my-card-wallet-guide__dots" aria-hidden="true">
                {steps.map((step, index) => (
                  <span
                    key={step.title}
                    className={
                      index === activeStep
                        ? 'my-card-wallet-guide__dot my-card-wallet-guide__dot--active'
                        : 'my-card-wallet-guide__dot'
                    }
                  />
                ))}
              </div>
            </div>

            <div className="my-card-wallet-guide__step-copy">
              <h3 className="my-card-wallet-guide__step-title">{currentStep.title}</h3>
              <ul className="my-card-wallet-guide__list">
                {currentStep.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            {isDetailsStep && (
              <div className="my-card-wallet-guide__details">
                <CardDetailsPanel
                  card={card}
                  cardCopy={cardCopy}
                  common={common}
                  revealedData={revealedData}
                  isRevealing={isRevealing}
                  onReveal={onReveal}
                  onCopy={onCopy}
                />
              </div>
            )}
          </article>
        </div>

        <footer className="my-card-wallet-guide__footer">
          <button
            type="button"
            className="my-card-wallet-guide__secondary"
            onClick={handlePrevious}
            disabled={isFirstStep}
          >
            {common.back}
          </button>
          <button
            type="button"
            className="my-card-wallet-guide__primary"
            onClick={handleNext}
          >
            {isLastStep ? common.done : common.continue}
          </button>
        </footer>
      </motion.section>
    </motion.div>
  );
}

function MyCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    card: cardCopy,
    common,
    deposit,
    t,
    walletGuide,
  } = useContext(TranslationContext);
  const { cardId } = useParams();
  const shouldReduceMotion = useReducedMotion();
  const { webApp } = useTelegram();
  const carouselCards = useCarouselCards();
  const heroRef = useRef(null);
  const incomingTransitionFrom = location.state?.cardTransitionFrom;
  const [cardEntryTransition, setCardEntryTransition] = useState(() => (
    incomingTransitionFrom && !shouldReduceMotion
      ? { from: incomingTransitionFrom, to: null }
      : null
  ));
  const [showRenameSheet, setShowRenameSheet] = useState(false);
  const [showFreezeSheet, setShowFreezeSheet] = useState(false);
  const [pendingCardStatus, setPendingCardStatus] = useState(null);
  const cardStatusRequestRef = useRef(false);
  const [showAppearanceSheet, setShowAppearanceSheet] = useState(false);
  const [appearancePreview, setAppearancePreview] = useState(null);
  const appearanceRequestRef = useRef(0);
  const [showCancelIssueSheet, setShowCancelIssueSheet] = useState(false);
  const [showWalletGuide, setShowWalletGuide] = useState(false);
  const [revealedData, setRevealedData] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);

  const requestedCardId = cardId ?? getSelectedCardId();
  const selectedCard =
    carouselCards.find((card) => card.id === requestedCardId) ?? carouselCards[0];
  const selectedId = selectedCard?.id;
  const displayedCardStyle = appearancePreview?.cardId === selectedId
    ? appearancePreview.style
    : selectedCard?.style;
  const detailCardId = selectedId || cardId;
  const isSelectedNotIssued = isNotIssuedCardStatus(selectedCard?.status);
  const isSelectedActivating = isActivatingCardStatus(selectedCard?.status);
  const isSelectedBlocked = isBlockedCardStatus(selectedCard?.status);
  const isSelectedFrozen = !isSelectedBlocked && isCardFrozen(selectedCard?.id);
  const isSelectedPending = isSelectedNotIssued || isSelectedActivating;
  const isSelectedActive = selectedCard?.status === 'active';
  const isSelectedPhysical = isPhysicalCardType(selectedCard?.type);
  const isFreezing = pendingCardStatus === 'freeze';
  const isUnfreezing = pendingCardStatus === 'unfreeze';
  const isCardEntryActive = Boolean(cardEntryTransition);
  const walletProvider = getWalletProvider(webApp);
  const selectedCardLabel = selectedCard?.name?.trim()
    || selectedCard?.label?.trim()
    || (isSelectedActivating
      ? cardCopy.activatingTitle
      : isSelectedPending ? cardCopy.pendingCard : cardCopy.main);
  const isSelectedIssueFundingCovered =
    isSelectedNotIssued && isCardIssueFundingCovered(selectedCard);
  const selectedIssueFundingProgress = getCardIssueFundingProgress(selectedCard);
  const shouldShowSelectedIssueProgress = Boolean(
    isSelectedNotIssued
    && !isSelectedIssueFundingCovered
    && selectedIssueFundingProgress.hasProgress,
  );
  const selectedIssueProgressText = shouldShowSelectedIssueProgress
    ? `${formatCardMoneyAmount(selectedIssueFundingProgress.receivedAmount)} / ${
      formatCardMoneyAmount(selectedIssueFundingProgress.requiredAmount)
    }`
    : '';
  const selectedCardDetailsText = isSelectedActivating
    ? cardCopy.activatingIssue
    : isSelectedNotIssued
      ? isSelectedIssueFundingCovered
        ? cardCopy.issuePaymentReceivedTitle
        : cardCopy.topUpToActivate
      : undefined;
  const selectedIssueTopUpAmount = isSelectedNotIssued
    ? calculateMinCardIssuePrice(selectedCard)
    : '0';
  const selectedIssueTopUpText = isSelectedNotIssued
    ? t('card.issueTopUpRequired', {
      amount: formatCardMoneyAmount(selectedIssueTopUpAmount),
    })
    : '';
  const selectedPendingPanelTitle = isSelectedIssueFundingCovered
    ? cardCopy.issuePaymentReceivedTitle
    : isSelectedActivating
      ? cardCopy.activatingTitle
      : cardCopy.topUpToActivate;
  const selectedPendingPanelText = isSelectedIssueFundingCovered
    ? cardCopy.issuePaymentReceivedText
    : isSelectedActivating
      ? cardCopy.activatingNotice
      : selectedIssueTopUpText;
  const displayedCardLabel = shouldShowSelectedIssueProgress
    ? cardCopy.completeActivation || cardCopy.topUpToActivate
    : selectedCardLabel;
  const displayedCardDetailsText = shouldShowSelectedIssueProgress
    ? selectedIssueProgressText
    : selectedCardDetailsText;
  const canContinueSelectedIssue = isSelectedNotIssued && !isSelectedIssueFundingCovered;
  const cardEntryFrame = cardEntryTransition?.to || null;
  const cardEntryInitialTransform = cardEntryFrame
    ? `translate3d(${cardEntryTransition.from.left - cardEntryFrame.left}px, ${
      cardEntryTransition.from.top - cardEntryFrame.top
    }px, 0) scale(${
      cardEntryTransition.from.width / Math.max(cardEntryFrame.width, 1)
    }, ${cardEntryTransition.from.height / Math.max(cardEntryFrame.height, 1)})`
    : null;

  useEffect(() => {
    if (!selectedId) return;

    setSelectedCardId(selectedId);
    setRevealedData(null);
    setIsRevealing(false);

    if (cardId && cardId !== selectedId) {
      navigate(`/cards/${selectedId}`, { replace: true });
    }
  }, [cardId, navigate, selectedId]);

  useEffect(() => {
    if (!detailCardId) return undefined;

    let isCancelled = false;

    refreshCardById(detailCardId).catch(() => {
      if (isCancelled) return;
    });

    return () => {
      isCancelled = true;
    };
  }, [detailCardId]);

  useLayoutEffect(() => {
    if (!cardEntryTransition || cardEntryTransition.to || !heroRef.current) {
      return;
    }

    const targetRect = heroRef.current.getBoundingClientRect();
    setCardEntryTransition((current) => {
      if (!current) return null;

      return {
        ...current,
        to: {
          left: targetRect.left,
          top: targetRect.top,
          width: targetRect.width,
          height: targetRect.height,
        },
      };
    });
  }, [cardEntryTransition]);

  const handleFreezeConfirm = async () => {
    if (!selectedCard || cardStatusRequestRef.current) return;

    const targetCardId = selectedCard.id;
    cardStatusRequestRef.current = true;
    setPendingCardStatus('freeze');

    try {
      await updateRemoteCardStatus(targetCardId, 'frozen');
      triggerImpactHaptic('light');
      setShowFreezeSheet(false);
    } catch {
      toast.error(CARD_ACTION_ERROR);
    } finally {
      cardStatusRequestRef.current = false;
      setPendingCardStatus(null);
    }
  };

  const handleUnfreeze = async () => {
    if (!selectedCard || cardStatusRequestRef.current) return;

    const targetCardId = selectedCard.id;
    cardStatusRequestRef.current = true;
    setPendingCardStatus('unfreeze');

    try {
      await updateRemoteCardStatus(targetCardId, 'active');
      triggerImpactHaptic('light');
    } catch {
      toast.error(CARD_ACTION_ERROR);
    } finally {
      cardStatusRequestRef.current = false;
      setPendingCardStatus(null);
    }
  };

  const handleDeposit = () => {
    cancelDepositFlow();
    setDepositCard(selectedCard.id);
    setDepositReturnPath(`${location.pathname}${location.search}${location.hash}`);
    navigate('/deposit', {
      state: { returnPath: `${location.pathname}${location.search}${location.hash}` },
    });
  };

  const handleOpenRenameSheet = () => {
    triggerImpactHaptic('light');
    setShowRenameSheet(true);
  };

  const handleRenameSave = async (nextName) => {
    try {
      await updateRemoteCardDetails(selectedCard.id, { name: nextName });
      triggerImpactHaptic('light');
      setShowRenameSheet(false);
    } catch {
      toast.error(CARD_ACTION_ERROR);
    }
  };

  const handleOpenAppearanceSheet = () => {
    triggerImpactHaptic('light');
    setShowAppearanceSheet(true);
  };

  const handleAppearanceSelect = async (style) => {
    if (!selectedCard || style === displayedCardStyle) return;

    const targetCardId = selectedCard.id;
    const requestId = appearanceRequestRef.current + 1;
    appearanceRequestRef.current = requestId;
    setAppearancePreview({ cardId: targetCardId, style });
    triggerImpactHaptic('light');

    try {
      await updateRemoteCardDetails(targetCardId, { style });

      if (appearanceRequestRef.current === requestId) {
        setAppearancePreview(null);
      }
    } catch {
      if (appearanceRequestRef.current === requestId) {
        setAppearancePreview(null);
        toast.error(CARD_ACTION_ERROR);
      }
    }
  };

  const handleOpenWalletGuide = () => {
    triggerImpactHaptic('light');
    setShowWalletGuide(true);
  };

  const handleChangePin = () => {
    triggerImpactHaptic('light');
    navigate(`/cards/${encodeURIComponent(selectedCard.id)}/change-pin`);
  };

  const handleContinueIssue = () => {
    triggerImpactHaptic('light');
    navigate(`/cards/new?continueIssue=1&hideBack=1&cardId=${encodeURIComponent(selectedCard.id)}`, {
      state: {
        fromPendingCard: true,
        hideBack: true,
        cardId: selectedCard.id,
      },
    });
  };

  const handleCloseWalletGuide = () => {
    triggerImpactHaptic('light');
    setShowWalletGuide(false);
  };

  const handleReveal = async () => {
    if (isRevealing || revealedData) return;

    triggerImpactHaptic('light');
    setIsRevealing(true);

    try {
      const cardWithSecret = await revealCardSecretData(selectedCard.id);
      const secretData = cardWithSecret.secretData;

      setRevealedData({
        pan: alignPanLastFour(secretData?.pan, selectedCard.lastFour),
        expiry: secretData?.expiry,
        cvv: secretData?.cvv,
        staticPassword: secretData?.staticPassword,
        pinCode: secretData?.pinCode,
      });
    } catch {
      toast.error(CARD_ACTION_ERROR);
    } finally {
      setIsRevealing(false);
    }
  };

  const handleCopyDetails = async (label, value) => {
    triggerImpactHaptic('light');
    const isCopied = await copyToClipboard(value);

    if (isCopied) {
      toast(t('common.valueCopied', { label }), {
        autoClose: 1600,
        className: 'app-toast app-toast--copy',
      });
      return;
    }

    toast.error(common.failedToCopy);
  };

  if (!selectedCard) {
    return null;
  }

  const handleBackToHome = () => {
    confirmCarouselCardReturn(selectedId);
    navigate('/home');
  };

  return (
    <section className="my-card">
      <div className="my-card__content">
        <MyCardHeader
          cardCopy={cardCopy}
          onBack={handleBackToHome}
          onEdit={isSelectedPending || isSelectedBlocked ? undefined : handleOpenRenameSheet}
        />

        <div
          ref={heroRef}
          className={
            isCardEntryActive
              ? 'my-card__hero my-card__hero--entry-hidden'
              : 'my-card__hero'
          }
        >
          <BankCard
            cardStyle={displayedCardStyle}
            cardType={selectedCard.type}
            showCardType
            size="detail"
            label={displayedCardLabel}
            balance={selectedCard.balance}
            detailsText={displayedCardDetailsText}
            heldBalance={selectedCard.uncreditedBalance}
            lastFour={selectedCard.lastFour}
            frozen={isSelectedFrozen}
            pending={isSelectedNotIssued}
            activating={isSelectedActivating}
            blocked={isSelectedBlocked}
          />
        </div>

        {cardEntryFrame && (
          <div className="my-card__entry-layer" aria-hidden="true">
            <motion.div
              className="my-card__entry-card"
              style={{
                left: cardEntryFrame.left,
                top: cardEntryFrame.top,
                width: cardEntryFrame.width,
                height: cardEntryFrame.height,
              }}
              initial={{ transform: cardEntryInitialTransform }}
              animate={{ transform: 'translate3d(0px, 0px, 0) scale(1, 1)' }}
              transition={CARD_ENTRY_TRANSITION}
              onAnimationComplete={() => setCardEntryTransition(null)}
            >
              <BankCard
                cardStyle={displayedCardStyle}
                cardType={selectedCard.type}
                showCardType
                size="detail"
                label={displayedCardLabel}
                balance={selectedCard.balance}
                detailsText={displayedCardDetailsText}
                heldBalance={selectedCard.uncreditedBalance}
                lastFour={selectedCard.lastFour}
                frozen={isSelectedFrozen}
                pending={isSelectedNotIssued}
                activating={isSelectedActivating}
                blocked={isSelectedBlocked}
              />
            </motion.div>
          </div>
        )}

        {isSelectedActive
          && (selectedCard.hasProcessingFunds || selectedCard.hasHeldFunds)
          && (
            <div className="my-card__balance-notices">
              {selectedCard.hasProcessingFunds && (
                <div
                  className="my-card__balance-notice my-card__balance-notice--processing"
                  role="status"
                >
                  {t('card.processingFundsNotice', {
                    amount: selectedCard.processingBalance,
                  })}
                </div>
              )}

              {selectedCard.hasHeldFunds && (
                <div
                  className="my-card__balance-notice my-card__balance-notice--held"
                  role="status"
                >
                  {t('card.heldFundsNotice', {
                    heldAmount: selectedCard.heldBalance,
                    minimumAmount: formatCardMoneyAmount(MIN_CARD_BALANCE_TOP_UP_USD),
                  })}
                </div>
              )}
            </div>
          )}

        {isSelectedActive && (
          <button type="button" className="my-card__deposit" onClick={handleDeposit}>
            <TopUpIcon className="my-card__deposit-icon" />
            {deposit.topUp}
          </button>
        )}

        <div
          key={`${selectedId}-${isSelectedFrozen ? 'frozen' : 'active'}`}
          className="my-card__panel"
        >
          {isSelectedPending ? (
            <PendingCardPanel
              card={selectedCard}
              cardCopy={cardCopy}
              title={selectedPendingPanelTitle}
              text={selectedPendingPanelText}
              onContinue={handleContinueIssue}
              showActions={canContinueSelectedIssue}
            />
          ) : isSelectedBlocked ? (
            <BlockedCardPanel cardCopy={cardCopy} />
          ) : isSelectedFrozen ? (
            <>
              <div className="my-card__frozen-notice">
                {cardCopy.frozenNotice}
              </div>
              <button
                type="button"
                className="my-card__unfreeze"
                disabled={isUnfreezing}
                aria-busy={isUnfreezing}
                onClick={handleUnfreeze}
              >
                {isUnfreezing ? (
                  <MiniPreloader
                    className="my-card__status-loader"
                    label={common.loading}
                  />
                ) : (
                  <>
                    <UnfreezeIcon className="my-card__unfreeze-icon" />
                    {cardCopy.unfreezeCard}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <CardDetailsPanel
                card={selectedCard}
                cardCopy={cardCopy}
                common={common}
                revealedData={revealedData}
                isRevealing={isRevealing}
                onReveal={handleReveal}
                onCopy={handleCopyDetails}
              />

              <CardIssueDetails
                card={selectedCard}
                cardCopy={cardCopy}
                common={common}
                onCopy={handleCopyDetails}
              />

              <div className="my-card__actions-grid">
                <button
                  type="button"
                  className="my-card__action"
                  onClick={() => setShowFreezeSheet(true)}
                >
                  <span className="my-card__action-icon" aria-hidden="true">
                    <FreezeIcon className="my-card__action-svg" />
                  </span>
                  <span className="my-card__action-copy">
                    <span className="my-card__action-title">{cardCopy.freezeAction}</span>
                    <span className="my-card__action-value">{cardCopy.freezeQuestion}</span>
                  </span>
                </button>

                <button
                  type="button"
                  className="my-card__action"
                  onClick={handleOpenAppearanceSheet}
                >
                  <span className="my-card__action-icon" aria-hidden="true">
                    <span
                      className={`my-card__appearance-swatch my-card__appearance-swatch--${displayedCardStyle}`}
                    />
                  </span>
                  <span className="my-card__action-copy">
                    <span className="my-card__action-title">{cardCopy.appearance}</span>
                    <span className="my-card__action-value">
                      {getCardStyleName(selectedCard.style, cardCopy.styles)}
                    </span>
                  </span>
                </button>
              </div>

              {isSelectedPhysical ? (
                <CardPinAction cardCopy={cardCopy} onClick={handleChangePin} />
              ) : null}

              <button
                type="button"
                className="my-card__wallet-add"
                onClick={handleOpenWalletGuide}
              >
                {walletProvider === 'apple' ? cardCopy.addToApplePay : cardCopy.addToGooglePay}
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showFreezeSheet && (
          <FreezeCardSheet
            cardNumber={formatMaskedPan(selectedCard.lastFour)}
            isSubmitting={isFreezing}
            onConfirm={handleFreezeConfirm}
            onCancel={() => {
              if (!isFreezing) setShowFreezeSheet(false);
            }}
          />
        )}

        {showCancelIssueSheet && (
          <CancelIssueSheet
            cardCopy={cardCopy}
            common={common}
            onConfirm={() => setShowCancelIssueSheet(false)}
            onCancel={() => setShowCancelIssueSheet(false)}
          />
        )}

        {showRenameSheet && (
          <RenameCardSheet
            currentName={selectedCardLabel}
            onClose={() => setShowRenameSheet(false)}
            onSave={handleRenameSave}
          />
        )}

        {showAppearanceSheet && (
          <CardAppearanceSheet
            currentStyle={displayedCardStyle}
            onClose={() => setShowAppearanceSheet(false)}
            onSelect={handleAppearanceSelect}
          />
        )}

        {showWalletGuide && (
          <WalletGuideModal
            provider={walletProvider}
            card={selectedCard}
            cardCopy={cardCopy}
            common={common}
            walletGuideCopy={walletGuide}
            revealedData={revealedData}
            isRevealing={isRevealing}
            onReveal={handleReveal}
            onCopy={handleCopyDetails}
            onClose={handleCloseWalletGuide}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export default MyCard;
